import React, { useState } from "react";
import PropTypes from "prop-types";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import arrayMove from "array-move";
import {
  Badge,
  ButtonDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Tooltip,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faInfoCircle,
  faCheckSquare,
} from "@fortawesome/free-solid-svg-icons";
import { faSquare } from "@fortawesome/free-regular-svg-icons";

const SortableItem = SortableElement(({ item, example }) => (
  <li className="builder__fields-column">
    <div className="builder__fields-column-th">
      <FontAwesomeIcon icon={faEllipsisV} className="mr-2" />
      <div>
        <div className="builder__fields-column-dataset">
          {item.dataset_name}:
        </div>
        <div className="builder__fields-column-field">{item.field_name}</div>
      </div>
    </div>
    <div className="builder__fields-column-td">{example}</div>
  </li>
));

const SortableList = SortableContainer(({ items }) => (
  <div className="builder__fields-table">
    <ul className="list-unstyled d-flex">
      {items.map((item, index) => (
        <SortableItem
          key={`field-${item.dataset_id}-${item.field_id}`}
          index={index}
          item={item}
          example="Example"
        />
      ))}
    </ul>
  </div>
));

const FieldMenuItem = ({ dataset, field, active }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggle = () => setTooltipOpen(!tooltipOpen);
  return (
    <span className="d-flex align-items-center">
      <FontAwesomeIcon icon={active ? faCheckSquare : faSquare} />
      <span className="mx-2">{field.alias}</span>
      <FontAwesomeIcon
        className="ms-auto"
        icon={faInfoCircle}
        id={`tooltip-${dataset.id}-${field.id}`}
      />
      <Tooltip
        placement="right"
        isOpen={tooltipOpen}
        target={`tooltip-${dataset.id}-${field.id}`}
        toggle={toggle}
      >
        {field.description}
      </Tooltip>
    </span>
  );
};

FieldMenuItem.propTypes = {
  dataset: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  field: PropTypes.shape({
    alias: PropTypes.string,
    dataset: PropTypes.string,
    description: PropTypes.string,
    filter: PropTypes.string,
    id: PropTypes.string,
    sort: PropTypes.string,
    type: PropTypes.string,
  }),
  active: PropTypes.bool,
};

FieldMenuItem.defaultProps = {
  dataset: null,
  field: null,
  active: null,
};

const DatasetDropDown = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggle = () => setMenuOpen(!menuOpen);
  return (
    <ButtonDropdown isOpen={menuOpen} toggle={toggle}>
      {children}
    </ButtonDropdown>
  );
};

DatasetDropDown.propTypes = {
  children: PropTypes.node.isRequired,
};

const Fields = ({
  fields,
  setFields,
  datasets,
  datasetsList,
  datasetsFieldsList,
}) => {
  const toggleSelectedField = (field) => {
    let newFields = [...fields];
    if (
      newFields.filter((e) => JSON.stringify(e) === JSON.stringify(field))
        .length > 0
    ) {
      newFields = newFields.filter(
        (e) => JSON.stringify(e) !== JSON.stringify(field)
      );
    } else {
      newFields.push(field);
    }
    setFields(newFields);
  };

  const toggleAllFields = (dataset) => {
    let newFields = [...fields];
    const allFields = datasetsFieldsList.filter(
      (e) => e.dataset === dataset.id
    );
    const count = fields.filter((e) => e.dataset_id === dataset.id).length;
    if (count === 0 || count !== allFields.length) {
      allFields.forEach((item) => {
        if (
          newFields.filter(
            (e) => e.dataset_id === dataset.id && e.field_id === item.id
          ).length < 1
        ) {
          newFields.push({
            dataset_id: dataset.id,
            dataset_name: dataset.title,
            field_id: item.id,
            field_name: item.alias,
          });
        }
      });
    } else {
      newFields = newFields.filter((e) => e.dataset_id !== dataset.id);
    }
    setFields(newFields);
  };

  const updateFieldsOrder = ({ oldIndex, newIndex }) => {
    let newFields = [...fields];
    newFields = arrayMove(newFields, oldIndex, newIndex);
    setFields(newFields);
  };

  return (
    <div className="builder__fields">
      <ul className="list-inline">
        {datasetsFieldsList &&
          datasetsList.map((dataset) => {
            if (datasets.includes(dataset.id)) {
              const total = datasetsFieldsList.filter(
                (e) => e.dataset === dataset.id
              ).length;
              const count = fields.filter((e) => e.dataset_id === dataset.id)
                .length;
              return (
                <li key={dataset.id} className="list-inline-item">
                  <DatasetDropDown
                    key={dataset.id}
                    id={`${dataset.id}--fieldsDropdown`}
                  >
                    <DropdownToggle color="primary" outline={count === 0} caret>
                      <span>{dataset.title}</span>
                      {count > 0 && (
                        <Badge color="light" className="ms-2">
                          <span style={{ color: "black" }}>{count}</span>
                        </Badge>
                      )}
                    </DropdownToggle>
                    <DropdownMenu id={`${dataset.id}--fieldsDropdownMenu`}>
                      <div className="builder__fields-menu">
                        {datasetsFieldsList
                          .filter((field) => {
                            return field.dataset === dataset.id;
                          })
                          .sort((a, b) => {
                            if (a.alias === b.alias) return 0;
                            return a.alias > b.alias ? 1 : -1;
                          })
                          .map((field) => {
                            const active =
                              fields.filter(
                                (e) =>
                                  e.dataset_id === dataset.id &&
                                  e.field_id === field.id
                              ).length > 0;
                            return (
                              <DropdownItem
                                key={field.id}
                                toggle={false}
                                active={active}
                                onClick={() => {
                                  toggleSelectedField({
                                    dataset_id: dataset.id,
                                    dataset_name: dataset.title,
                                    field_id: field.id,
                                    field_name: field.alias,
                                  });
                                }}
                              >
                                <FieldMenuItem
                                  dataset={dataset}
                                  field={field}
                                  active={active}
                                />
                              </DropdownItem>
                            );
                          })}
                      </div>
                      <div className="builder__fields-all">
                        <DropdownItem
                          onClick={() => {
                            toggleAllFields(dataset);
                          }}
                        >
                          {total !== count ? "Select all" : "Remove All"}
                        </DropdownItem>
                      </div>
                    </DropdownMenu>
                  </DatasetDropDown>
                </li>
              );
            }
            return false;
          })}
      </ul>
      {datasetsFieldsList && fields.length > 0 && (
        <div className="my-5">
          <div className="builder__label">Column Order</div>
          <SortableList axis="x" items={fields} onSortEnd={updateFieldsOrder} />
        </div>
      )}
    </div>
  );
};

Fields.propTypes = {
  fields: PropTypes.array.isRequired,
  setFields: PropTypes.func.isRequired,
  datasets: PropTypes.array.isRequired,
  datasetsList: PropTypes.array,
  datasetsFieldsList: PropTypes.array,
};

Fields.defaultProps = {
  datasetsList: null,
  datasetsFieldsList: null,
};

export default Fields;
